class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay' || evt.target.closest('#CartDrawer-Overlay')) {
        this.close();
      }
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Si ya está abierto (ej. se agrega otro producto con el drawer visible),
    // no volver a capturar scrollY: con body position:fixed sería 0 y al
    // cerrar mandaría al usuario al inicio de la página.
    const alreadyOpen = this.classList.contains('active');

    const trapDrawerFocus = () => {
      const containerToTrapFocusOn = document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__close');
      if (focusElement) focusElement.focus({ preventScroll: true });
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    if (alreadyOpen) {
      trapDrawerFocus();
      return;
    }

    setTimeout(() => this.classList.add('animate', 'active'));

    this.addEventListener('transitionend', trapDrawerFocus, { once: true });

    this._scrollY = window.scrollY;
    document.body.style.top = `-${this._scrollY}px`;
    document.body.classList.add('scroll-disabled');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('scroll-disabled');
    document.body.style.top = '';
    if (this._scrollY !== undefined) {
      window.scrollTo({ top: this._scrollY, behavior: 'instant' });
      this._scrollY = undefined;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // Remover clases is-empty
    this.classList.remove('is-empty');
    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.remove('is-empty');

    this.productId = parsedState.id;

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const newDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');

      // Actualizar SOLO el wrapper de items (no todo #CartDrawer-CartItems)
      // Esto preserva la barra de free shipping y otros elementos
      const newItemsWrapper = newDoc.querySelector('.drawer__cart-items-wrapper');
      const currentItemsWrapper = this.querySelector('.drawer__cart-items-wrapper');
      if (newItemsWrapper && currentItemsWrapper) {
        currentItemsWrapper.innerHTML = newItemsWrapper.innerHTML;
      }

      // La visibilidad del estado vacío la controla el CSS vía la clase is-empty
      // (removida arriba). Limpiar cualquier estilo inline heredado para no
      // sobrescribir esa regla.
      const emptyElement = this.querySelector('.drawer__inner-empty');
      if (emptyElement) emptyElement.style.removeProperty('display');

      // Actualizar valores de texto con animación
      this.updateTextValueWithAnimation('#CartDrawer-Subtotal', newDoc);
      this.updateTextValue(newDoc, '.cart-drawer__totals-row:not(.cart-drawer__totals-row--subtotal) .cart-drawer__totals-value');

      // Si el footer no existe, crearlo desde el nuevo HTML
      if (!this.querySelector('.drawer__footer')) {
        const newFooter = newDoc.querySelector('.drawer__footer');
        if (newFooter && drawerInner) {
          const footerClone = newFooter.cloneNode(true);
          // Asegurar que el botón no esté disabled
          const btn = footerClone.querySelector('#CartDrawer-Checkout');
          if (btn) btn.removeAttribute('disabled');
          drawerInner.appendChild(footerClone);
        }
      }

      const checkoutBtn = this.querySelector('#CartDrawer-Checkout');
      if (checkoutBtn) checkoutBtn.removeAttribute('disabled');

      // Asegurar que el header, footer y free shipping bar estén visibles
      const header = this.querySelector('.drawer__header');
      const footer = this.querySelector('.drawer__footer');
      const freeShippingBar = this.querySelector('#FreeShippingBar');
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
      if (freeShippingBar && parsedState.total_price > 0) {
        freeShippingBar.classList.add('active');
      }

      // [FIX] Update Cross Selling Slider
      const newCrossSelling = newDoc.querySelector('#CartDrawer-CrossSelling');
      const currentCrossSelling = this.querySelector('#CartDrawer-CrossSelling');

      if (newCrossSelling && currentCrossSelling) {
        currentCrossSelling.innerHTML = newCrossSelling.innerHTML;
        currentCrossSelling.style.display = '';
        if (window.CartDrawerCrossSelling) window.CartDrawerCrossSelling.init();
      } else if (currentCrossSelling && !newCrossSelling) {
        currentCrossSelling.style.display = 'none';
      }
    }

    // Actualizar cart-icon-bubble
    this.updateCartIconBubble(parsedState);

    // Update free shipping bar
    if (typeof window.FreeShippingBar !== 'undefined' && parsedState.total_price !== undefined) {
      window.FreeShippingBar.update(parsedState.total_price);
    }

    // Abrir el drawer
    setTimeout(() => this.open(), 10);
  }

  updateTextValue(newDoc, selector) {
    const newEl = newDoc.querySelector(selector);
    const currentEl = this.querySelector(selector);
    if (newEl && currentEl) currentEl.textContent = newEl.textContent;
  }

  updateTextValueWithAnimation(selector, newDoc) {
    const newEl = newDoc.querySelector(selector);
    const currentEl = this.querySelector(selector);
    if (newEl && currentEl) {
      currentEl.textContent = newEl.textContent;
      currentEl.classList.remove('updating');
    }
  }

  updateCartIconBubble(parsedState) {
    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble) {
        const newDoc = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
        // Buscar el contenido dentro de .shopify-section
        const newContent = newDoc.querySelector('.shopify-section');
        if (newContent) {
          iconBubble.innerHTML = newContent.innerHTML;
        }
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }

  renderSections(parsedState, changedLine) {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;

    if (parsedState.sections && parsedState.sections['cart-drawer']) {
      const newDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');

      const currentItems = cartDrawer.querySelector('.drawer__cart-items-wrapper');
      const newItems = newDoc.querySelector('.drawer__cart-items-wrapper');
      const currentCount = currentItems ? currentItems.querySelectorAll('.cart-drawer-item').length : 0;
      const newCount = newItems ? newItems.querySelectorAll('.cart-drawer-item').length : 0;
      const itemWasRemoved = newCount < currentCount;

      if (changedLine && !itemWasRemoved && currentItems && newItems) {
        const currentLineItem = currentItems.querySelector(`#CartDrawer-Item-${changedLine}`);
        const newLineItem = newItems.querySelector(`#CartDrawer-Item-${changedLine}`);
        if (currentLineItem && newLineItem) {
          currentLineItem.innerHTML = newLineItem.innerHTML;
          currentLineItem.dataset.unitPrice = newLineItem.dataset.unitPrice || '';
          currentLineItem.dataset.linePrice = newLineItem.dataset.linePrice || '';
        } else {
          currentItems.innerHTML = newItems.innerHTML;
        }
      } else if (newItems && currentItems) {
        currentItems.innerHTML = newItems.innerHTML;
      }

      // Actualizar totales
      const newTotals = newDoc.querySelector('.cart-drawer__totals');
      const currentTotals = cartDrawer.querySelector('.cart-drawer__totals');
      if (newTotals && currentTotals) {
        currentTotals.innerHTML = newTotals.innerHTML;
      }

      const subtotalEl = cartDrawer.querySelector('#CartDrawer-Subtotal');
      if (subtotalEl) subtotalEl.classList.remove('updating');

      // Actualizar botón checkout
      const newCheckoutBtn = newDoc.querySelector('#CartDrawer-Checkout');
      const currentCheckoutBtn = cartDrawer.querySelector('#CartDrawer-Checkout');
      if (newCheckoutBtn && currentCheckoutBtn) {
        currentCheckoutBtn.toggleAttribute('disabled', newCheckoutBtn.hasAttribute('disabled'));
      }

      const isEmpty = parsedState.item_count === 0;
      cartDrawer.classList.toggle('is-empty', isEmpty);
      const inner = cartDrawer.querySelector('.drawer__inner');
      if (inner) inner.classList.toggle('is-empty', isEmpty);
      this.classList.toggle('is-empty', isEmpty);

      // El estado vacío lo controla el CSS vía is-empty (toggleada arriba);
      // limpiar estilos inline heredados para no sobrescribir la regla.
      const emptyElement = cartDrawer.querySelector('.drawer__inner-empty');
      if (emptyElement) emptyElement.style.removeProperty('display');

      const header = cartDrawer.querySelector('.drawer__header');
      const footer = cartDrawer.querySelector('.drawer__footer');

      if (header) header.style.display = '';
      if (footer) footer.style.display = '';

      if (isEmpty) {
        if (currentCheckoutBtn) currentCheckoutBtn.setAttribute('disabled', 'disabled');
      } else {
        if (currentCheckoutBtn) currentCheckoutBtn.removeAttribute('disabled');
      }

      // Actualizar Cross Selling
      const newCrossSelling = newDoc.querySelector('#CartDrawer-CrossSelling');
      const currentCrossSelling = cartDrawer.querySelector('#CartDrawer-CrossSelling');

      if (newCrossSelling && currentCrossSelling) {
        currentCrossSelling.innerHTML = newCrossSelling.innerHTML;
        currentCrossSelling.style.display = '';
        if (window.CartDrawerCrossSelling) window.CartDrawerCrossSelling.init();
      } else if (newCrossSelling && !currentCrossSelling) {
        const totalsEl = cartDrawer.querySelector('.cart-drawer__totals');
        if (totalsEl) totalsEl.insertAdjacentElement('afterend', newCrossSelling);
        if (window.CartDrawerCrossSelling) window.CartDrawerCrossSelling.init();
      } else if (currentCrossSelling && !newCrossSelling) {
        currentCrossSelling.remove();
      }
    }

    // Actualizar cart-icon-bubble
    if (parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const iconBubble = document.getElementById('cart-icon-bubble');
      if (iconBubble) {
        const newDoc = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
        const newContent = newDoc.querySelector('.shopify-section');
        if (newContent) iconBubble.innerHTML = newContent.innerHTML;
      }
    }

    // Update free shipping bar
    if (typeof window.FreeShippingBar !== 'undefined' && parsedState.total_price !== undefined) {
      window.FreeShippingBar.update(parsedState.total_price);
    }
  }

  updateTextValue(container, newDoc, selector) {
    const newEl = newDoc.querySelector(selector);
    const currentEl = container.querySelector(selector);
    if (newEl && currentEl) currentEl.textContent = newEl.textContent;
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

// Free Shipping Progress Bar Updates
window.FreeShippingBar = {
  observer: null,

  // Parse subtotal text to get amount in cents
  parseSubtotal: function () {
    const subtotalEl = document.getElementById('CartDrawer-Subtotal');
    if (!subtotalEl) return 0;
    // moneyTextToCents (cart.js) maneja separador de miles: "S/ 1,179.00 PEN"
    if (typeof window.moneyTextToCents === 'function') {
      return window.moneyTextToCents(subtotalEl.textContent);
    }
    const match = (subtotalEl.textContent || '').replace(/[^\d.,]/g, '').replace(',', '.');
    return Math.round((parseFloat(match) || 0) * 100);
  },

  // cartTotalCents: opcional, en centavos (ej. total_price de la respuesta AJAX del carrito).
  // Si no se pasa, se estima leyendo el subtotal del drawer.
  // Actualiza TODAS las barras presentes en la página (drawer y/o PDP), no solo #FreeShippingBar.
  update: function (cartTotalCents) {
    const bars = document.querySelectorAll('[data-free-shipping-bar]');
    if (!bars.length) return;

    // Sin valor explícito del servidor, no reflejar el subtotal optimista
    // (clase 'updating'): la barra solo se mueve con el total real confirmado.
    if (typeof cartTotalCents !== 'number') {
      const subtotalEl = document.getElementById('CartDrawer-Subtotal');
      if (subtotalEl && subtotalEl.classList.contains('updating')) return;
    }

    const cartTotal = typeof cartTotalCents === 'number' ? cartTotalCents : this.parseSubtotal();

    bars.forEach((bar) => {
      const threshold = parseInt(bar.dataset.threshold || 0, 10);
      if (!threshold) return;

      const remaining = threshold - cartTotal;
      const progressPercent = Math.min(100, Math.max(0, (cartTotal * 100) / threshold));
      const isAchieved = cartTotal >= threshold;
      // ceil: con S/ 0.40 restantes debe decir "te falta S/ 1", nunca "S/ 0"
      const remainingSoles = Math.ceil(Math.max(0, remaining) / 100);
      const messageAchieved = bar.dataset.messageAchieved || '¡Felicidades! Tienes envío gratis';
      const messageProgress = bar.dataset.messageProgress || 'Te faltan {remaining} para obtener envío gratis';
      const isPdp = bar.dataset.variant === 'pdp';

      const fill = document.getElementById(bar.id + '-Fill');
      if (fill) fill.style.width = progressPercent + '%';

      bar.classList.toggle('achieved', isAchieved);
      if (!isPdp) {
        // La barra del PDP siempre está visible; solo la del drawer se muestra/oculta según el carrito.
        bar.classList.toggle('active', cartTotal > 0);
      }

      const remainingHtml = isPdp
        ? '<span class="free-shipping-bar__em">S/ ' + remainingSoles + '</span>'
        : '<b>S/ ' + remainingSoles + '</b>';
      const targetEl = document.getElementById(bar.id + (isPdp ? '-Hint' : '-Message'));
      if (targetEl) {
        targetEl.innerHTML = isAchieved ? messageAchieved : messageProgress.replace('{remaining}', remainingHtml);
      }
    });
  },

  // Initialize observer to watch subtotal changes
  init: function () {
    const self = this;

    // Destroy existing observer if any
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Actualización inicial (cubre también la barra del PDP, independiente del drawer)
    this.update();

    const subtotalEl = document.getElementById('CartDrawer-Subtotal');
    if (!subtotalEl) return;

    // Watch for subtotal text changes
    this.observer = new MutationObserver(function () {
      self.update();
    });

    this.observer.observe(subtotalEl, {
      childList: true,
      characterData: true,
      subtree: true
    });
  }
};

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  window.FreeShippingBar.init();
});

// Re-init when cart drawer becomes active (force update)
(function () {
  const initObserver = function () {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) {
      setTimeout(initObserver, 100);
      return;
    }

    new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        if (mutation.attributeName === 'class' && cartDrawer.classList.contains('active')) {
          // Small delay to ensure DOM is updated
          setTimeout(function () {
            window.FreeShippingBar.init();
          }, 100);
        }
      });
    }).observe(cartDrawer, { attributes: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initObserver);
  } else {
    initObserver();
  }
})();

const CART_OPEN_HASH = '#cart-open';

function openCartDrawerFromHash() {
  if (window.location.hash !== CART_OPEN_HASH) return;
  const drawer = document.querySelector('cart-drawer');
  if (!drawer) return;
  drawer.open();
  const cleanUrl = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, '', cleanUrl);
}

function initCartOpenHash() {
  openCartDrawerFromHash();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartOpenHash);
} else {
  initCartOpenHash();
}
window.addEventListener('hashchange', openCartDrawerFromHash);
